
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface GenerationState {
  isGenerating: boolean;
  collectionId: string | null;
  startedAt: Date | null;
}

export function useCollectionGenerationState() {
  const [generationState, setGenerationState] = useState<GenerationState>({
    isGenerating: false,
    collectionId: null,
    startedAt: null
  });

  const startGeneration = useCallback((collectionId: string) => {
    console.log(`🚀 Starting generation tracking for collection: ${collectionId}`);
    setGenerationState({
      isGenerating: true,
      collectionId,
      startedAt: new Date()
    });

    // Create immediate progress tracking record
    const createProgressRecord = async () => {
      try {
        // Try to get existing product count for more accurate total
        const { data: products } = await supabase
          .from('product_descriptions')
          .select('id')
          .eq('collection_id', collectionId);
        
        const totalItems = products?.length || 1;
        const progressId = `upload-${collectionId}`;
        
        await supabase.from('progress_tracking').upsert({
          id: progressId,
          collection_id: collectionId,
          status: 'processing',
          processed_items: 0,
          total_items: totalItems,
          current_stage: 'preparing',
          current_action: 'Preparing for AI generation...',
          process_type: 'product_upload',
          notification_sent: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
        
        console.log(`✅ Progress tracking record created for: ${collectionId} (${totalItems} items)`);
      } catch (error) {
        console.error('❌ Failed to create immediate progress record:', error);
      }
    };

    createProgressRecord();

    // Auto-stop after 5 minutes if no progress updates
    setTimeout(() => {
      setGenerationState(prev => {
        if (prev.collectionId === collectionId && prev.isGenerating) {
          console.log(`⏰ Auto-stopping generation tracking for: ${collectionId}`);
          return { isGenerating: false, collectionId: null, startedAt: null };
        }
        return prev;
      });
    }, 300000); // 5 minutes
  }, []);

  const stopGeneration = useCallback((collectionId?: string) => {
    setGenerationState(prev => {
      if (!collectionId || prev.collectionId === collectionId) {
        console.log(`🛑 Stopping generation tracking for: ${prev.collectionId}`);
        return { isGenerating: false, collectionId: null, startedAt: null };
      }
      return prev;
    });
  }, []);

  const isGeneratingCollection = useCallback((collectionId: string) => {
    return generationState.isGenerating && generationState.collectionId === collectionId;
  }, [generationState]);

  return {
    generationState,
    startGeneration,
    stopGeneration,
    isGeneratingCollection
  };
}
